import { zodResolver } from '@hookform/resolvers/zod'
import type { HttpError } from '@refinedev/core'
import { useGo, useList, useNotification } from '@refinedev/core'
import type { Tenant, UserCreateInput } from '@sirius/contracts/admin'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useAdminClient } from '~/api/adminClientContext'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form'
import { Input } from '~/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { errorMessage } from '~/providers/errors'
import type { CreateUserFormOutput, CreateUserFormValues } from './userActions'
import {
  createUserFieldError,
  createUserFormSchema,
  NO_TENANT,
  TENANT_ROLE_OPTIONS,
  toCreateUserInput,
} from './userActions'
import {
  platformRoleChoices,
  roleValue,
  tenantRoleLabel,
  toPlatformRole,
} from './userFormat'
import { useUserRefresh } from './useUserRefresh'

const EMPTY: CreateUserFormValues = {
  name: '',
  email: '',
  password: '',
  platformRole: null,
  tenantId: NO_TENANT,
  role: 'member',
}

/**
 * "Create user": a new account with a password staff hand over (no
 * invitation), staff or a tenant user, optionally a member of one tenant
 * right away. On success it opens the new user's page.
 */
export function CreateUserDialog() {
  const { t } = useTranslation()
  const client = useAdminClient()
  const refresh = useUserRefresh()
  const go = useGo()
  const { open } = useNotification()
  const [isOpen, setIsOpen] = useState(false)
  const form = useForm<CreateUserFormValues, unknown, CreateUserFormOutput>({
    resolver: zodResolver(createUserFormSchema),
    defaultValues: EMPTY,
  })
  const tenants = useList<Tenant, HttpError>({
    resource: 'tenants',
    pagination: { mode: 'off' },
    sorters: [{ field: 'name', order: 'asc' }],
    queryOptions: { enabled: isOpen },
  })
  const create = useMutation({
    mutationFn: (input: UserCreateInput) => client.users.create(input),
    onSuccess: async (user) => {
      await refresh(user.id)
      setIsOpen(false)
      open?.({
        type: 'success',
        message: t('usersPage.create.created', { name: user.name }),
      })
      go({ to: `/platform/users/${user.id}` })
    },
    onError: (error) => {
      const fieldError = createUserFieldError(error, t)
      if (fieldError) {
        form.setError(fieldError.field, { message: fieldError.message })
      }
    },
  })
  const showsError = create.isError && !createUserFieldError(create.error, t)
  const hasTenant = form.watch('tenantId') !== NO_TENANT

  const onOpenChange = (next: boolean) => {
    // no closing while the user is created: its outcome shows here
    if (!next && create.isPending) {
      return
    }
    if (next) {
      form.reset(EMPTY)
      create.reset()
    }
    setIsOpen(next)
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <DialogTrigger asChild>
        <Button size="sm">{t('usersPage.create.trigger')}</Button>
      </DialogTrigger>
      <DialogContent>
        <Form {...form}>
          <form
            noValidate
            onSubmit={form.handleSubmit((values) =>
              create.mutate(toCreateUserInput(values)),
            )}
            className="grid gap-4"
          >
            <DialogHeader>
              <DialogTitle>{t('usersPage.create.title')}</DialogTitle>
              <DialogDescription>
                {t('usersPage.create.description')}
              </DialogDescription>
            </DialogHeader>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('usersPage.create.name')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('usersPage.create.email')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      autoComplete="off"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('usersPage.create.password')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="password"
                      autoComplete="new-password"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="platformRole"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('usersPage.create.platformRole')}</FormLabel>
                  <Select
                    value={roleValue(field.value)}
                    onValueChange={(value) =>
                      field.onChange(toPlatformRole(value))
                    }
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {platformRoleChoices(t).map(({ value, label }) => (
                        <SelectItem
                          key={value}
                          value={value}
                        >
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid grid-cols-[1fr_9rem] items-start gap-2">
              <FormField
                control={form.control}
                name="tenantId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('usersPage.create.tenant')}</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={NO_TENANT}>
                          {t('usersPage.create.noTenant')}
                        </SelectItem>
                        {tenants.result.data.map((tenant) => (
                          <SelectItem
                            key={tenant.id}
                            value={tenant.id}
                          >
                            {tenant.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      {t('usersPage.create.moreMemberships')}
                    </FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('usersPage.create.tenantRole')}</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={!hasTenant}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TENANT_ROLE_OPTIONS.map((role) => (
                          <SelectItem
                            key={role}
                            value={role}
                          >
                            {tenantRoleLabel(role, t)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>
            {showsError && (
              <p
                role="alert"
                className="text-destructive text-sm"
              >
                {t('usersPage.create.error', {
                  message: errorMessage(create.error),
                })}
              </p>
            )}
            <DialogFooter>
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={create.isPending}
                >
                  {t('usersPage.cancel')}
                </Button>
              </DialogClose>
              <Button
                type="submit"
                disabled={create.isPending}
              >
                {t('usersPage.create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
